import React from 'react';

import { pickCurrentNavLink, type NavLinkClaim } from '../../utils/pickCurrentNavLink';

/** Undo one claim. Returned by `claim`, so a caller never repeats the id. */
export type NavLinkClaimDisposer = () => void;

/** What a navigation group gives to the links inside it. */
export interface NavLinkGroupValue {
    /** Claim the page in view. The same id claimed twice replaces the first claim. */
    claim: (claim: NavLinkClaim) => NavLinkClaimDisposer;
    /** Id of the link that points at the page in view, or null when no link does. */
    winnerId: string | null;
}

const NavLinkGroupContext = React.createContext<NavLinkGroupValue | null>(null);

export interface NavLinkGroupProps {
    children?: React.ReactNode;
}

/**
 * A group of navigation links, of which exactly one points at the page in view.
 *
 * A link that matches the page claims it, with its matched URL length. The group picks the longest
 * claim. It knows nothing about routes: each link matches its own route, where the route is known.
 * Only a link that matches claims, so the group holds a few claims, and never the whole sidebar.
 *
 * The nearest group wins, so a group nested in another one picks on its own, and the links of the
 * outer group are not its candidates.
 *
 * @family Router
 */
export const NavLinkGroup: React.FC<NavLinkGroupProps> = ({ children }) => {
    const claims = React.useRef(new Map<string, NavLinkClaim>());
    const [, bump] = React.useReducer((version: number) => version + 1, 0);

    /**
     * Stable for the life of the group. A write bumps the version, so the group renders again and
     * reads the new claims. The links claim in layout effects, and React runs the bumps in one batch
     * after them, so the group picks once per commit, before paint.
     */
    const claim = React.useCallback((next: NavLinkClaim): NavLinkClaimDisposer => {
        claims.current.set(next.id, next);
        bump();

        return () => {
            claims.current.delete(next.id);
            bump();
        };
    }, []);

    // Read during render on purpose: every write to the ref bumps the version, so this render sees it.
    const winnerId = pickCurrentNavLink(Array.from(claims.current.values()))?.id ?? null;

    const value = React.useMemo<NavLinkGroupValue>(() => ({ claim, winnerId }), [claim, winnerId]);

    return <NavLinkGroupContext.Provider value={value}>{children}</NavLinkGroupContext.Provider>;
};

NavLinkGroup.displayName = 'NavLinkGroup';

/** Read the nearest navigation group, or null outside any group. */
export const useNavLinkGroup = (): NavLinkGroupValue | null => React.useContext(NavLinkGroupContext);
